import { useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import { updateMenuSettings } from "services/menu/rest";
import { UpdateMenuSettingsRequestDto } from "services/menu/restRequests";
import { useConstants } from "shared/hooks/useConstants";
import { useCurrencyId } from "shared/hooks/useCurrencyId";
import { useLocale } from "shared/hooks/useLocale";
import { useMenuOptions } from "shared/hooks/useMenuOptions";
import { useMenus } from "shared/hooks/useMenus";
import { useMenuSettings } from "shared/hooks/useMenuSettings";
import { useOrganizationBranchDetails } from "shared/hooks/useOrganizationBranchDetails";
import { createCurrencyFormatter } from "shared/lib/currencyFormatter";
import {
    isPaymentSoon,
    isTariffLevelActive,
    tariffStatusOf,
    timeLeftLabel,
} from "shared/lib/tariffStatus";
import { useNotificationSnackbar } from "shared/ui";
import { TariffVariant } from "modules/settings/models";

import { useAppearanceState } from "features/MenuSettings/hooks/useAppearanceState";
import { useChatsState } from "features/MenuSettings/hooks/useChatsState";
import { useDeliveryState } from "features/MenuSettings/hooks/useDeliveryState";
import { useGeneralInfoState } from "features/MenuSettings/hooks/useGeneralInfoState";
import { useMenuFlagsState } from "features/MenuSettings/hooks/useMenuFlagsState";
import { usePaymentsState } from "features/MenuSettings/hooks/usePaymentsState";

/*
* Store-less состояние формы настроек меню: данные меню — из REST, локальный стейт
* разложен по доменам (общее / флаги / оформление / доставка / оплата / чаты).
* */
export const useMenuSettingsState = () => {
    const params = useParams<{ menuId: string }>();
    const menuId = parseInt(params.menuId);

    const { refetch: refreshMenus } = useMenus();
    const { refetch: refreshMenuOptions } = useMenuOptions();

    const locale = useLocale();
    const currencyId = useCurrencyId();

    const { settings, loading: settingsLoading, error: settingsError } = useMenuSettings(menuId);
    const { constants, loading: constantsLoading, error: constantsError } = useConstants();
    const { branch: branchDetails } = useOrganizationBranchDetails();

    // Цены доставки форматируются по decimalPlaces валюты — ждём константы вместе с меню.
    const currency = constants ? constants.currencies.find((it) => it.id === currencyId) : null;
    const currencyFormatter = useMemo(
        () => createCurrencyFormatter(currency, locale),
        [currency, locale]
    );

    const loading = settingsLoading || constantsLoading;
    const error = settingsError || constantsError;

    const general = useGeneralInfoState(settings);
    const menuFlags = useMenuFlagsState(settings);
    const appearance = useAppearanceState(settings);
    const delivery = useDeliveryState(settings, currencyId);
    const payments = usePaymentsState(settings, currencyId);
    const chats = useChatsState(settings);

    const notify = useNotificationSnackbar();

    const [saving, setSaving] = useState<boolean>(false);
    const [tariffPrompt, setTariffPrompt] = useState<TariffVariant | null>(null);

    // Плашка и остаток жизни меню считаются тем же кодом, что и кнопка тарифа в шапке.
    const tariffState = useMemo(() => {
        if (!branchDetails) return { isOverdue: false, menusAliveText: "" };

        const now = Date.now();
        const status = tariffStatusOf(branchDetails.tariff, now);

        if (status === "overdue") {
            return {
                isOverdue: true,
                menusAliveText: timeLeftLabel(branchDetails.tariff.menusActiveTillDate, now),
            };
        }

        return {
            isOverdue: false,
            menusAliveText:
                status === "active" && isPaymentSoon(branchDetails.tariff, now)
                    ? timeLeftLabel(branchDetails.tariff.nextPaymentDate, now)
                    : "",
        };
    }, [branchDetails]);

    /*
    * Тарифные гейты: если уровня не хватает, переключатель не меняется, а вместо этого
    * открывается предложение поднять тариф. Возвращаем признак «действие разрешено».
    * */
    const ensureTariff = (level: TariffVariant): boolean => {
        /*
        * Тариф приезжает отдельным запросом и позже самой формы. Пока его нет, действие не
        * пропускаем, но и предложение поднять тариф не показываем — иначе быстрый клик по
        * тумблеру натыкается на апселл у заведения, которому он не нужен.
        * */
        if (!branchDetails) return false;

        if (!isTariffLevelActive(branchDetails.tariff, level, Date.now())) {
            setTariffPrompt(level);
            return false;
        }

        return true;
    };

    const buildRequest = (): UpdateMenuSettingsRequestDto => ({
        name: general.name,
        description: general.description,
        addressDetails: {
            mapsUrl: general.mapsUrl,
            title: general.addressTitle,
            region: general.region,
        },
        contactDetails: general.contacts,
        photos: appearance.photos,
        workingSchedule: general.workingSchedule,
        availableLanguages: { enabledLocales: menuFlags.enabledLocales },
        themeSettings: appearance.theme,
        deliverySettings: delivery.deliveryList,
        paymentSettings: payments.paymentList,
        chats: chats.chats,
        infoMessage: menuFlags.infoMessage,
        isMenuEnabled: menuFlags.flags.isMenuEnabled,
        isCartEnabled: menuFlags.flags.isCartEnabled,
        isOrderCreationEnabled: menuFlags.flags.isOrderCreationEnabled,
        isPromoCodesEnabled: menuFlags.flags.isPromoCodesEnabled,
        staffCalls: menuFlags.staffCalls,
        isLoyaltyProgramEnabled: menuFlags.flags.isLoyaltyProgramEnabled,
    });

    const save = async () => {
        setSaving(true);
        try {
            await updateMenuSettings(menuId, buildRequest());
            /*
            * Название и обложка видны на карточке в списке меню, а название — ещё и в
            * селектах меню. Списки кешируются двумя разными хуками (useMenus со
            * счётчиками и облегчённый useMenuOptions), поэтому сбрасываем оба: иначе
            * переименованное меню осталось бы под старым именем в выпадашках.
            * */
            refreshMenus();
            refreshMenuOptions();
            // Со страницы не уходим: форма разбита на табы, и после сохранения на одном
            // из них пользователь обычно продолжает работу на соседних.
            notify.showSuccess("Настройки меню сохранены");
        } catch (e) {
            notify.showError("Не удалось сохранить настройки", e);
        } finally {
            setSaving(false);
        }
    };

    return {
        // meta
        menuId,
        loading,
        error,
        saving,
        locale,
        currencyId,
        currencyFormatter,
        isOldPriceLabelEnabled: settings ? settings.isOldPriceLabelEnabled : false,
        menuType: settings ? settings.type : null,
        hasExternalIntegration: settings ? settings.hasExternalIntegration : false,
        iikoTerminals: settings ? settings.iikoTerminals : [],
        isTariffOverdue: tariffState.isOverdue,
        menusAliveText: tariffState.menusAliveText,
        // состояние по доменам
        general,
        menuFlags,
        appearance,
        delivery,
        payments,
        chats,
        // тариф
        tariffPrompt,
        setTariffPrompt,
        ensureTariff,
        // уведомления фичи: свои, без стора
        notify,
        // действия
        save,
    };
};

export type MenuSettingsState = ReturnType<typeof useMenuSettingsState>;
